import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  Square3Stack3DIcon,
  PlusIcon,
  UserCircleIcon,
  PrinterIcon,
  ArrowRightStartOnRectangleIcon,
  BuildingStorefrontIcon,
  TrophyIcon,
  QueueListIcon,
  Cog6ToothIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { Button, ThemeToggle, Badge } from '../ui';
import { SwipeableView } from '../ui/SwipeableView';
import { AddPlantModal } from '../plants/AddPlantModal';
import { LabelQueueModal } from '../plants/LabelQueueModal';
import { MobileBottomNav } from './MobileBottomNav';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useLabelQueueCount } from '../../hooks/useLabelQueueQueries';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [showAddPlantModal, setShowAddPlantModal] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const isMobile = useIsMobile();
  const { data: queueCount } = useLabelQueueCount();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Plants', href: '/plants', icon: Square3Stack3DIcon },
    { name: 'Seeds', href: '/seeds', icon: SparklesIcon },
    { name: 'Sources', href: '/sources', icon: BuildingStorefrontIcon },
    { name: 'Labels', href: '/labels', icon: PrinterIcon },
    { name: 'Badges', href: '/badges', icon: TrophyIcon },
  ];

  // Routes for swipe navigation (excluding labels which is less frequently used)
  const swipeRoutes = ['/dashboard', '/plants', '/seeds', '/sources', '/profile'];

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Logout failed, but we continue with cleanup
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="bg-card shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex">
              <Link
                to="/dashboard"
                className="flex items-center px-2 text-xl font-bold text-primary"
              >
                🌱 PlantQR
              </Link>

              <div className="hidden sm:ml-8 sm:flex sm:space-x-6">
                {navigation.map(item => {
                  const isActive =
                    location.pathname === item.href ||
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href));

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                      }`}
                    >
                      <item.icon className="h-4 w-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}

                {/* Quick actions */}
                <div className="flex items-center space-x-4 border-l border-border pl-6 ml-2">
                  <button
                    onClick={() => setShowAddPlantModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Plant
                  </button>

                  <button
                    onClick={() => setShowQueueModal(true)}
                    className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors relative"
                  >
                    <QueueListIcon className="h-4 w-4 mr-1" />
                    Queue
                    {queueCount && queueCount.count > 0 && (
                      <Badge
                        variant="default"
                        size="sm"
                        className="ml-1 px-1.5 py-0.5 text-xs min-w-5 h-5 flex items-center justify-center"
                      >
                        {queueCount.count}
                      </Badge>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              <ThemeToggle />

              {/* Admin Panel button - only show for admin users */}
              {user?.isAdmin && (
                <Link to="/admin" aria-label="Admin Panel">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={
                      location.pathname.startsWith('/admin') ? 'bg-primary/10 text-primary' : ''
                    }
                  >
                    <Cog6ToothIcon className="h-5 w-5" />
                  </Button>
                </Link>
              )}

              <Button variant="ghost" size="icon" aria-label="User profile">
                <a href="/profile">
                  <UserCircleIcon className="h-5 w-5" />
                </a>
              </Button>

              <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Logout">
                <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      {isMobile ? (
        <SwipeableView routes={swipeRoutes} enabled={isMobile}>
          <main className="pb-16">{children}</main>
        </SwipeableView>
      ) : (
        <main>{children}</main>
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav onAddPlantClick={() => setShowAddPlantModal(true)} />}

      {/* Add Plant Modal */}
      <AddPlantModal isOpen={showAddPlantModal} onClose={() => setShowAddPlantModal(false)} />

      {/* Label Queue Modal */}
      <LabelQueueModal isOpen={showQueueModal} onClose={() => setShowQueueModal(false)} />
    </div>
  );
}
